import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// A toggle slider that controls whether timesheet cell edits save immediately
// (ON, default) or are queued and committed in one batched request when the
// user clicks Save (OFF).  Flipping from OFF back to ON flushes any queued
// edits first so no work is lost.
const AutoSaveToggle = ({
  autoSave,
  setAutoSave,
  flushPendingSaves,
  pendingSaves,
  mobile
}) => {
  const handleChange = async e => {
    const next = e.target.checked;
    if (next === true && pendingSaves && Object.keys(pendingSaves).length > 0) {
      // flush first so queued edits aren't lost when auto-save resumes
      try {
        await flushPendingSaves();
      } catch (err) {
        // flushPendingSaves handles its own error reporting; don't block the toggle
      }
    }
    setAutoSave(next);
  };

  const isOn = autoSave !== false;
  const titleText = isOn
    ? 'Auto-save is ON. Each cell edit is saved immediately.'
    : 'Auto-save is OFF. Cell edits are queued - click Save to commit them.';

  if (mobile) {
    // Stop click propagation: the parent .actions-button div uses an onClick
    // that toggles the mobile dashboard closed.  Without this, flipping the
    // switch would also dismiss the panel which makes it unusable.
    const stop = e => e.stopPropagation();
    return (
      <MobileRow
        title={titleText}
        className="actions-sub-row autosave-row"
        onClick={stop}>
        <LabelText>Auto-save</LabelText>
        <Switch onClick={stop}>
          <input
            type="checkbox"
            checked={isOn}
            onChange={handleChange}
            aria-label="Auto-save"
          />
          <Slider />
        </Switch>
      </MobileRow>
    );
  }

  return (
    <Wrapper title={titleText} className="desktop-control">
      <LabelText>Auto-save</LabelText>
      <Switch>
        <input
          type="checkbox"
          checked={isOn}
          onChange={handleChange}
          aria-label="Auto-save"
        />
        <Slider />
      </Switch>
    </Wrapper>
  );
};

AutoSaveToggle.propTypes = {
  autoSave: PropTypes.bool,
  setAutoSave: PropTypes.func.isRequired,
  flushPendingSaves: PropTypes.func,
  pendingSaves: PropTypes.object,
  mobile: PropTypes.bool
};

const Wrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: 10px;
  cursor: pointer;
`;

// Sits inside the mobile dashboard tray (dark blue background, full-width
// stacked controls).  Matches the spacing of the actions-sub-button siblings
// so it slots into the flow without looking bolted on.
const MobileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 80%;
  margin: 0 auto 12px auto;
  padding: 6px 12px;
  height: 35px;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.12);
  color: white;
  font-size: 13px;
  cursor: default;
`;

const LabelText = styled.span`
  font-weight: normal;
  user-select: none;
`;

// Visually-hidden checkbox sits on top so click + keyboard + screen reader
// still work while we paint the slider chrome underneath.
const Switch = styled.label`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  margin: 0;

  input {
    opacity: 0;
    width: 0;
    height: 0;
    margin: 0;
  }

  input:checked + span {
    background-color: #5cb85c;
  }

  input:checked + span::before {
    transform: translateX(20px);
  }

  input:focus + span {
    box-shadow: 0 0 2px 2px rgba(92, 184, 92, 0.5);
  }
`;

const Slider = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  border-radius: 20px;
  transition: background-color 0.2s ease-in-out;

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    height: 16px;
    width: 16px;
    background-color: white;
    border-radius: 50%;
    transition: transform 0.2s ease-in-out;
  }
`;

export default AutoSaveToggle;
